import { Modal } from 'components/layouts/modal/modal'
import { memo, useCallback, useEffect, useState } from 'react'
import { useProjectMemberEntryDialog } from './useProjectMemberEntryDialog'
import { ProjectMemberEntryTab } from './components/projectMemberEntryTab/projectMemberEntryTab'
import { CrewButton } from 'components/elements/crewButton/crewButton'
import { ProjectMemberEntryDialogTabs } from 'enums/app'
import { AddExistingUser } from './components/addExistingUser/addExistingUser'
import { InviteNewUsers } from './components/inviteNewUsers/inviteNewUsers'
import { CrewScrollView } from 'components/devextreme/crewScrollView'
import { useShowApiErrors } from 'hooks/useShowApiErrors'
import { useTranslation } from '@crew/modules/i18n'
import { useToast } from 'hooks/useToast'
import { useParams } from 'react-router-dom'
import { useGetInviteableUsersQuery } from '@crew/apis/user/userApis'
import { ContractPlan, InvitationRoleType } from '@crew/enums/app'
import { useAppSelector } from 'states/hooks'
import { GetInviteableUsersResponse } from '@crew/apis/user/models/getInviteableUsers/response'

export type PropsMode = {
  title: string
  isOpen: boolean
  onClose: () => void
}

export const ProjectMemberEntryDialog = memo((props: PropsMode) => {
  const {
    selectedNewUsers,
    setSelectedNewUsers,
    selectedExistingUsers,
    setSelectedExistingUsers,
    insertProjectMembers,
    insertProjectMemberPendings,
    projectMemberEntryDialogContext,
    isLoadingInsertProjectMembers,
    isLoadingInsertProjectMemberPending,
  } = useProjectMemberEntryDialog()

  const { projectId } = useParams()

  const { t } = useTranslation()
  const toast = useToast()
  const [showApiErrors] = useShowApiErrors()

  const projectSettingMemberEventMessage = useAppSelector(
    (state) => state.app.projectSettingMemberEventMessage
  )
  const currentPlan = useAppSelector((state) => state.app.currentPlan)

  // 招待可能なユーザー数を取得
  const {
    data: getInviteableUsersResult,
    refetch: refetchInviteableUsersResult,
  } = useGetInviteableUsersQuery()

  // refetch inviteable users/external users when tenant setting member updated
  useEffect(() => {
    if (projectSettingMemberEventMessage) {
      refetchInviteableUsersResult()
    }
  }, [
    currentPlan,
    projectSettingMemberEventMessage,
    refetchInviteableUsersResult,
  ])

  const [selectedIndex, setSelectedIndex] = useState<number>(
    ProjectMemberEntryDialogTabs.ExistingUsers.id
  )

  // タブ選択時、選択状態(LocalState)を更新
  const handleTabsSelectionChanged = useCallback((selectedIndex: number) => {
    setSelectedIndex(selectedIndex)
  }, [])

  //close dialog
  const handleCloseDialog = useCallback(() => {
    props.onClose()

    //clear context state
    setSelectedExistingUsers([])
    setSelectedNewUsers([])
  }, [props, setSelectedExistingUsers, setSelectedNewUsers])

  // Event handle when Add or Invite member is clicked
  const handleAddOrInviteMemberButtonClick = useCallback(async () => {
    if (selectedIndex === ProjectMemberEntryDialogTabs.ExistingUsers.id) {
      //add existing member to project
      try {
        //if project id is null or selected existing users is empty, return
        if (!projectId || selectedExistingUsers.length === 0) return

        // Execute insert project member process
        await insertProjectMembers(projectId)

        toast.success(t('message.project.projectMemberRegistered'))

        //clear context state
        setSelectedExistingUsers([])

        //close dialog
        props.onClose()
      } catch (error) {
        showApiErrors(error)
      }
    } else {
      //invite new member to project
      try {
        //if project id is null or selected new users is empty, return
        if (!projectId || selectedNewUsers.length === 0) return

        const externalUsersCount = selectedNewUsers.filter(
          (user) => user.tenantRoleType === InvitationRoleType.External.key
        ).length

        const internalUsersCount = selectedNewUsers.length - externalUsersCount

        const inviteableUsers =
          getInviteableUsersResult ??
          ({
            inviteableUsers: 0,
            inviteableExternalUsers: 0,
            currentExternalUsers: 0,
            currentUsers: 0,
            remainingInviteableUsers: 0,
            remainingInviteableExternalUsers: 0,
            totalRemainingInviteableUsers: 0,
          } satisfies GetInviteableUsersResponse)

        // 内部ユーザーの空き枠数が足りているか
        if (internalUsersCount > inviteableUsers.remainingInviteableUsers) {
          toast.error(
            t('message.apiError.inviteTenantUsersExceedLimit', {
              remainingUser: inviteableUsers.remainingInviteableUsers,
            })
          )
          return
        }

        // 外部ユーザーの空き枠数が足りているか
        if (
          externalUsersCount > inviteableUsers.remainingInviteableExternalUsers
        ) {
          toast.error(
            t('message.apiError.inviteExternalUserLimitExceeded', {
              inviteableExternalUserCount:
                inviteableUsers.inviteableExternalUsers,
              currentExternalUserCount: inviteableUsers.currentExternalUsers,
              exceededExternalUserCount:
                externalUsersCount -
                inviteableUsers.remainingInviteableExternalUsers,
            })
          )
          return
        }

        // 内部ユーザー・外部ユーザー共通の空き枠数が足りているか。
        // 内部ユーザー単体、外部ユーザー単体での枠数上限判定は既に行っているので、
        // ここの判定でひっかかるのは、フリープランなど、内部ユーザーと外部ユーザーの枠が合算されて上限判定される場合のみ。
        if (
          internalUsersCount + externalUsersCount >
          inviteableUsers.totalRemainingInviteableUsers
        ) {
          toast.error(
            // 内部ユーザ・外部ユーザ共通なので、両者を区別しない「ユーザー数が制限を超えた」というメッセージを表示する
            t('message.apiError.inviteTenantUsersExceedLimit', {
              remainingUser: inviteableUsers.totalRemainingInviteableUsers,
            })
          )
          return
        }

        // Execute insert project member pending process
        await insertProjectMemberPendings(projectId)

        toast.success(t('message.project.projectMemberRegistered'))

        //clear context state
        setSelectedNewUsers([])

        //close dialog
        props.onClose()
      } catch (error) {
        console.log(error)
        showApiErrors(error)
      }
    }
  }, [
    getInviteableUsersResult,
    insertProjectMemberPendings,
    insertProjectMembers,
    projectId,
    props,
    selectedExistingUsers.length,
    selectedIndex,
    selectedNewUsers,
    setSelectedExistingUsers,
    setSelectedNewUsers,
    showApiErrors,
    t,
    toast,
  ])

  // Render Add or Invite member form
  const renderAddOrInviteMemberForm = useCallback(() => {
    switch (selectedIndex) {
      case ProjectMemberEntryDialogTabs.ExistingUsers.id:
        return <AddExistingUser />
      case ProjectMemberEntryDialogTabs.NewUsers.id:
        return <InviteNewUsers />
      default:
        return null
    }
  }, [selectedIndex])

  return (
    <projectMemberEntryDialogContext.Provider
      value={{
        selectedExistingUsers,
        setSelectedExistingUsers,
        selectedNewUsers,
        setSelectedNewUsers,
      }}
    >
      <Modal
        title={props.title}
        isOpen={props.isOpen}
        onClose={handleCloseDialog}
      >
        <div className="flex flex-col gap-y-3 h-full">
          <p className="text-md">{t('label.addOrInviteMemberDescription')}</p>
          {/* モーダルの最小幅を制限し、一覧タブやデータグリッドの各入力項目が正しく表示されるようにする */}
          <div className="overflow-x-auto flex-grow">
            <div className="min-w-[725px] flex flex-col gap-y-2.5 h-full">
              <CrewScrollView>
                <div className="flex">
                  <ProjectMemberEntryTab
                    selectedIndex={selectedIndex}
                    tabsSelectionChanged={handleTabsSelectionChanged}
                  />
                </div>

                {/* タブ選択状態に合わせてタブ表示切り替え */}
                <div className="flex flex-col gap-y-5">
                  {renderAddOrInviteMemberForm()}
                </div>
              </CrewScrollView>
              {/* Dialog action */}
              <div className="flex items-center gap-x-3">
                {selectedIndex === ProjectMemberEntryDialogTabs.NewUsers.id && (
                  <p className="mr-auto">
                    {/* 招待可能なユーザー数 */}
                    {t('label.hintForAllowInviteableUsers', {
                      remaining:
                        getInviteableUsersResult?.remainingInviteableUsers ?? 0,
                      total: getInviteableUsersResult?.inviteableUsers ?? 0,
                    })}

                    {/* 招待可能な外部ユーザー数（有償プランの場合のみ表示） */}
                    {(currentPlan === ContractPlan.Professional ||
                      currentPlan === ContractPlan.Standard) && (
                      <>
                        <br />
                        {t('label.hintForAllowInviteableExternalUsers', {
                          remaining:
                            getInviteableUsersResult?.remainingInviteableExternalUsers ??
                            0,
                          total:
                            getInviteableUsersResult?.inviteableExternalUsers ??
                            0,
                        })}
                      </>
                    )}
                  </p>
                )}

                <CrewButton
                  text={
                    selectedIndex ===
                    ProjectMemberEntryDialogTabs.ExistingUsers.id
                      ? t('action.addToProject')
                      : t('action.inviteToProject')
                  }
                  type="primary"
                  onClick={handleAddOrInviteMemberButtonClick}
                  className="ml-auto"
                  disabled={
                    isLoadingInsertProjectMembers ||
                    isLoadingInsertProjectMemberPending
                  }
                />

                <CrewButton
                  text={t('action.cancel')}
                  type="normal"
                  stylingMode="outlined"
                  onClick={handleCloseDialog}
                />
              </div>
            </div>
          </div>
        </div>
      </Modal>
    </projectMemberEntryDialogContext.Provider>
  )
})
